import { useState } from 'react'

import { cap } from '../../../State'
import { translations } from '../../../translations'
import { goelettRilevante, midofficeInUso, statoConnessione, testoDestinazione } from './Functions'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Riepilogo dei valori che Nora legge da altre fonti (09 §9.3), in sola lettura e
 * richiuso di default: serve a sapere dove cambiarli, non si cambiano da qui.
 * Mostra solo ciò che il DTO `overview` fornisce: per le policy Community e i
 * limiti tecnici indica la fonte, non i valori (nessuna chiamata extra).
 * Una connessione che nessuna configurazione usa è «non usata», non un avviso.
 * @param {Object} props - Proprietà
 * @param {Object} props.overview - DTO overview
 * @returns {import('react').JSX.Element}
 */
export const RiepilogoEsistente = ({ overview }) => {
  const [aperto, setAperto] = useState(false)
  const { environment, communities = [], providerStatus = {} } = overview
  const predefinita = communities.find((c) => c.isDefault)
  const dinamicoDemo = providerStatus['dinamico-demo']
  const goelett = providerStatus.goelett
  const statoMidoffice = statoConnessione(dinamicoDemo, t, midofficeInUso(overview))
  const statoGoelett = statoConnessione(goelett, t, goelettRilevante(overview))

  const righe = [
    {
      voce: t.cnRigaEnvironment,
      valore: `${environment.label} (${environment.code})`,
      fonte: t.cnFonteEnvironment
    },
    {
      voce: t.cnRigaDestinazioneDinamico,
      valore: testoDestinazione(environment.dynamicTarget, t),
      fonte: t.cnFonteEnvironment
    },
    {
      voce: t.cnRigaCommunityPredefinita,
      valore: predefinita?.label || t.cnNessuna,
      fonte: t.cnFonteCommunity
    },
    {
      voce: t.cnRigaPolicyCommunity,
      valore: t.cnPolicyCommunityValore,
      fonte: t.cnFonteCommunity
    },
    {
      voce: t.cnRigaModelloLimiti,
      // Il nome viene dal deploy della piattaforma; senza chiave il modello non parte.
      valore: overview.platformModel?.model
        ? [
            overview.platformModel.model,
            overview.platformModel.reasoningEffort,
            overview.platformModel.usable ? null : t.cnModelloSenzaChiave
          ]
            .filter(Boolean)
            .join(' · ')
        : t.cnGestitiDalDeploy,
      fonte: t.cnFonteTecnica
    },
    {
      voce: t.cnRigaConnessioneMidoffice,
      valore: (
        <>
          <span className={`nbadge ${statoMidoffice.badge}`}>{statoMidoffice.label}</span>{' '}
          {dinamicoDemo?.configured
            ? testoDestinazione(environment.dynamicTarget, t)
            : midofficeInUso(overview)
              ? t.cnConnessioneMidofficeAuto
              : ''}
        </>
      ),
      fonte: t.cnFonteTecnica
    },
    {
      voce: t.cnRigaConnessioneGoelett,
      valore: (
        <>
          <span className={`nbadge ${statoGoelett.badge}`}>{statoGoelett.label}</span>{' '}
          {goelett?.configured ? testoDestinazione(goelett.destination, t) : ''}
        </>
      ),
      fonte: t.cnFonteTecnica
    }
  ]

  return (
    <section className="ncard" aria-labelledby="cn-titolo-riepilogo" data-testid="cn-riepilogo">
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-eye" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title" id="cn-titolo-riepilogo">
            {t.cnRiepilogoTitolo}
          </h3>
          <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>{t.cnRiepilogoHint}</span>
        </div>
        <div className="ncard__head-actions">
          <button
            type="button"
            className="nbtn nbtn--ghost nbtn--sm"
            aria-expanded={aperto}
            aria-controls="cn-riepilogo-corpo"
            onClick={() => setAperto((old) => !old)}
          >
            {aperto ? t.cnNascondi : t.cnMostra}
          </button>
        </div>
      </div>
      {aperto && (
        <div id="cn-riepilogo-corpo" className="ncard__body" style={{ overflowX: 'auto' }}>
          <table className="ntable" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{t.cnVoce}</th>
                <th style={{ textAlign: 'left' }}>{t.cnValore}</th>
                <th style={{ textAlign: 'left' }}>{t.cnProvenienza}</th>
              </tr>
            </thead>
            <tbody>
              {righe.map(({ voce, valore, fonte }) => (
                <tr key={voce}>
                  <td>{voce}</td>
                  <td>{valore}</td>
                  <td>
                    <span className="nbadge">{fonte}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export default RiepilogoEsistente
